import { CentralEdgeDevice, CentralEdgeModem, RemoteCommand, ModemCapabilities, LegacyModemCapabilities } from '@simcheck/shared-types';
import { auth } from '@/config/firebase';
import { CENTRAL_API_URL, CENTRAL_WS_URL, FUNCTIONS_URL } from '@/config/env';

/**
 * A modem that exists physically on the edge device but failed the AT
 * probe or went unresponsive during operation. The edge's recovery loop
 * is still trying to heal it (re-probe every 60s; USB unbind/rebind
 * after 5 min; give up after 30 min).
 */
export interface DegradedModemStatus {
  devpath: string;
  kernel_name: string;
  usb_vendor?: string | null;
  usb_product?: string | null;
  usb_product_string?: string | null;
  first_degraded_ms?: number;
  last_probe_ms?: number;
  age_seconds?: number;
  attempts?: number;
  reason?: string;
  rebind_attempted?: boolean;
  /**
   * Terminal state label from the gateway. Currently:
   *   "firmware_corrupt_edl_mode" — modem is in Qualcomm emergency download
   *     mode; no amount of re-probe or USB rebind will recover it. Needs a
   *     Firehose flash of signed Quectel firmware. Surface as "Needs
   *     firmware flash" in the UI.
   */
  state?: string;
  requires_firmware_flash?: boolean;
}

// CENTRAL_API_URL: central-server (raw Cloud Run). Holds the live edge-device + modem
// registry (populated by Pi heartbeats).

// Runtime API for capability suggestions — Firebase Functions base.
const RUNTIME_API_URL = FUNCTIONS_URL;

// For development, you can override to use local: http://localhost:3000/api

/**
 * Get auth headers with Firebase ID token
 */
export async function getAuthHeaders(): Promise<HeadersInit> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }
  
  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
}

// Client-side cache to prevent duplicate concurrent calls (e.g. TopBar + page both requesting)
let devicesCache: { data: CentralEdgeDevice[]; timestamp: number } | null = null;
const DEVICES_CACHE_TTL_MS = 15000; // 15 seconds

/**
 * Edge Device Service
 *
 * Frontend API client for interacting with edge devices (Raspberry Pis)
 * and their modems through the central management system.
 */
export const edgeDeviceService = {
  /**
   * Get all edge devices (system-level infrastructure, not organization-scoped)
   */
  async getDevices(forceRefresh = false): Promise<CentralEdgeDevice[]> {
    // Return cached data if fresh (unless force refresh)
    if (!forceRefresh && devicesCache && Date.now() - devicesCache.timestamp < DEVICES_CACHE_TTL_MS) {
      return devicesCache.data;
    }

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${CENTRAL_API_URL}/devices?_t=${Date.now()}`,
        { headers, cache: 'no-store' }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to fetch edge devices' }));
        throw new Error(error.message || 'Failed to fetch edge devices');
      }

      const data = await response.json();
      // Central server may return devices directly or wrapped in { devices }
      const devices = Array.isArray(data) ? data : data.devices;

      devicesCache = { data: devices, timestamp: Date.now() };
      return devices;
    } catch (error) {
      console.error('Error fetching edge devices:', error);
      throw error;
    }
  },


  /**
   * Get a specific edge device by ID
   */
  async getDevice(deviceId: string): Promise<CentralEdgeDevice> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${CENTRAL_API_URL}/devices/${deviceId}`,
        { headers }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to fetch edge device' }));
        throw new Error(error.message || 'Failed to fetch edge device');
      }

      const data = await response.json();
      return data.device || data;
    } catch (error) {
      console.error('Error fetching edge device:', error);
      throw error;
    }
  },

  /**
   * Get all modems for a specific edge device.
   *
   * Legacy callers can keep using this — it still returns just the active
   * modems array. Use `getDeviceModemsWithDegraded` when you need to
   * surface stuck/recovering modems (admin UI).
   */
  async getModems(deviceId: string): Promise<CentralEdgeModem[]> {
    const { modems } = await this.getDeviceModemsWithDegraded(deviceId);
    return modems;
  },

  /**
   * Get active modems plus the edge's degraded-modem list. Degraded modems
   * are physically present but failed the AT probe or went unresponsive;
   * the edge's recovery loop is still trying to heal them. Surfacing this
   * lets admins see "stuck" modems instead of them silently disappearing.
   */
  async getDeviceModemsWithDegraded(deviceId: string): Promise<{
    modems: CentralEdgeModem[];
    degradedModems: DegradedModemStatus[];
  }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${CENTRAL_API_URL}/devices/${deviceId}/modems`,
        { headers }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to fetch modems' }));
        throw new Error(error.message || 'Failed to fetch modems');
      }

      const data = await response.json();
      // Backward-compat: older central-server responses are a bare array.
      if (Array.isArray(data)) {
        return { modems: data, degradedModems: [] };
      }
      return {
        modems: Array.isArray(data?.modems) ? data.modems : [],
        degradedModems: Array.isArray(data?.degraded_modems) ? data.degraded_modems : [],
      };
    } catch (error) {
      console.error('Error fetching modems:', error);
      throw error;
    }
  },

  /**
   * Forget a known modem on a device — admin-initiated DELETE that removes
   * the entry from the device's `known_modems` subcollection. The modem
   * will disappear from the UI until the gateway reports it again (if it
   * physically exists). Intended for use when hardware has been swapped out.
   */
  async forgetModem(deviceId: string, identifier: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${CENTRAL_API_URL}/devices/${deviceId}/known_modems/${encodeURIComponent(identifier)}`,
      { method: 'DELETE', headers }
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to forget modem' }));
      throw new Error(error.message || 'Failed to forget modem');
    }
  },

  /**
   * Get suggested capabilities for a modem (from database or auto-detection)
   */
  async getSuggestedCapabilities(model: string, manufacturer: string): Promise<LegacyModemCapabilities | null> {
    // For now, use local logic until backend is deployed
    // This matches the detection logic in ModemCapabilitiesService
    const modelLower = (model || '').toLowerCase();
    const manufacturerLower = (manufacturer || '').toLowerCase();
    
    console.log(`📊 Lookup details: model="${model}", manufacturer="${manufacturer}"`);
    
    if (manufacturerLower.includes('quectel')) {
      console.log('✅ Detected Quectel manufacturer');
      if (modelLower.includes('em120') || modelLower.includes('em12')) {
        console.log('✅ Matched EM120R-GL model');
        return {
          supports5G: false,
          supports4G: true,
          supports3G: true,
          supports2G: true,
          supportsVoLTE: false,
          supportseSIM: false,
          maxDownloadSpeed: 150,
          maxUploadSpeed: 50,
        };
      }
      if (modelLower.includes('eg95')) {
        return {
          supports5G: false,
          supports4G: true,
          supports3G: true,
          supports2G: true,
          supportsVoLTE: true,
          supportseSIM: false,
          maxDownloadSpeed: 150,
          maxUploadSpeed: 50,
        };
      }
      if (modelLower.includes('bg95')) {
        return {
          supports5G: false,
          supports4G: true,
          supports3G: false,
          supports2G: false,
          supportsVoLTE: false,
          supportseSIM: false,
          maxDownloadSpeed: 10,
          maxUploadSpeed: 5,
        };
      }
    }
    
    console.log('❌ No match found');
    return null;
  },

  /**
   * Update modem capabilities (legacy - use updateModemSettings for new code)
   */
  async updateModemCapabilities(deviceId: string, modemId: string, capabilities: LegacyModemCapabilities | ModemCapabilities): Promise<void> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${CENTRAL_API_URL}/devices/${deviceId}/modems/${modemId}/capabilities`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify({ capabilities })
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to update modem capabilities' }));
        throw new Error(error.message || 'Failed to update modem capabilities');
      }
    } catch (error) {
      console.error('Error updating modem capabilities:', error);
      throw error;
    }
  },

  /**
   * Update modem settings (ModemModel attachment and custom capabilities)
   *
   * @param deviceId - Edge device ID
   * @param modemId - Modem ID (IMEI or internal ID)
   * @param settings - Settings to update
   *   - modemModelId: Reference to modem_models collection (for capability inheritance)
   *   - modemModelName: Cached display name (e.g., "Quectel EG95")
   *   - capabilities: Custom capability overrides (only used if hasCustomCapabilities is true)
   *   - hasCustomCapabilities: Flag indicating whether custom capabilities are set
   */
  async updateModemSettings(
    deviceId: string,
    modemId: string,
    settings: {
      // `null` explicitly unlinks (and tombstones auto-link, #191).
      // `undefined` is dropped by JSON.stringify and leaves the link untouched.
      modemModelId?: string | null;
      modemModelName?: string | null;
      capabilities?: ModemCapabilities;
      hasCustomCapabilities?: boolean;
      priority?: number;
      /**
       * Per-modem eSIM provisioning method:
       *  - `'sgp32'`    → SGP.32 cloud orchestration (existing default)
       *  - `'lpac_at'`  → lpac on the Pi over modem AT (consumer-RSP)
       *  - `'manual'`   → never auto-provision
       *  - `null` / `''` → clear; fall through to org default → 'manual'
       */
      eSimProvisioningMethod?: 'sgp32' | 'lpac_at' | 'manual' | null | '';
      /**
       * Admin opt-in: run AT+CFUN=1,1 (full reset) on clean-on-release instead
       * of the soft COPS=0 + RAT reset. Default off. Only for modems known to
       * tolerate a CFUN reboot.
       */
      cfunResetOnRelease?: boolean;
      /**
       * Admin "disable" switch. When true the modem is never handed out for any
       * test, even while online. Default off.
       */
      disabled?: boolean;
      /**
       * Connectivity mode (#252): `'persistent'` | `'hunt'` enables /
       * reconfigures (send `connectivityConfig` alongside), `null` turns it off.
       */
      connectivityMode?: 'persistent' | 'hunt' | null;
      connectivityConfig?: Record<string, any>;
    }
  ): Promise<void> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${CENTRAL_API_URL}/devices/${deviceId}/modems/${modemId}/settings`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify(settings)
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to update modem settings' }));
        throw new Error(error.message || 'Failed to update modem settings');
      }
    } catch (error) {
      console.error('Error updating modem settings:', error);
      throw error;
    }
  },

  /**
   * Send an AT command to a modem
   */
  async sendCommand(
    deviceId: string, 
    modemImei: string, 
    command: string, 
    timeout: number = 30
  ): Promise<RemoteCommand> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${CENTRAL_API_URL}/devices/${deviceId}/command`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            modemId: modemImei,
            command,
            timeout
          })
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to send command' }));
        throw new Error(error.message || 'Failed to send command');
      }

      const data = await response.json();
      return data.command || data;
    } catch (error) {
      console.error('Error sending command:', error);
      throw error;
    }
  },

  /**
   * Get command history for an edge device
   */
  async getCommandHistory(deviceId: string, limit: number = 50): Promise<RemoteCommand[]> {
    try {
      // Note: This endpoint may not exist in central-server yet
      // May need to query Firestore directly or add this endpoint
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${CENTRAL_API_URL}/devices/${deviceId}/commands?limit=${limit}`,
        { headers }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to fetch command history' }));
        throw new Error(error.message || 'Failed to fetch command history');
      }

      const data = await response.json();
      return Array.isArray(data) ? data : data.commands;
    } catch (error) {
      console.error('Error fetching command history:', error);
      throw error;
    }
  },

  /**
   * Get modem responses (AT command responses) for an edge device
   */
  async getModemResponses(deviceId: string, limit: number = 100): Promise<any[]> {
    try {
      // Note: This endpoint may not exist in central-server yet
      // Central server uses Firebase Realtime Database for responses
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${CENTRAL_API_URL}/devices/${deviceId}/responses?limit=${limit}`,
        { headers }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to fetch modem responses' }));
        throw new Error(error.message || 'Failed to fetch modem responses');
      }

      const data = await response.json();
      return Array.isArray(data) ? data : data.responses;
    } catch (error) {
      console.error('Error fetching modem responses:', error);
      throw error;
    }
  },

  /**
   * Update device settings
   */
  async updateDeviceSettings(
    deviceId: string,
    settings: {
      name?: string;
      address?: string;
      latitude?: number;
      longitude?: number;
      locationSource?: 'manual' | 'gps' | 'network';
      locationId?: string | null;  // For hierarchical location assignment
      locationName?: string;
      countryId?: string;
      regionId?: string;
      deviceTier?: 'production' | 'demo' | 'lab';
      skipEsimProvisioning?: boolean;
      allowOrgAdminShell?: boolean;
      ownerOrgId?: string | null;  // Super-admin only: re-home device to an org, or null = shared
      backhaulModemImei?: string | null;  // #247: designate the device's own uplink modem (null clears)
      backhaulApn?: string | null;
    }
  ): Promise<void> {
    try {
      // Note: This endpoint may not exist in central-server yet
      // May need to add PATCH /api/devices/:id endpoint
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${CENTRAL_API_URL}/devices/${deviceId}`,
        {
          method: 'PATCH',
          headers,
          body: JSON.stringify(settings)
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to update device settings' }));
        throw new Error(error.message || 'Failed to update device settings');
      }

      // Invalidate cache so next getDevices() fetches fresh data
      devicesCache = null;
    } catch (error) {
      console.error('Error updating device settings:', error);
      throw error;
    }
  },

  /**
   * Delete an edge device
   */
  async deleteDevice(deviceId: string): Promise<void> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${CENTRAL_API_URL}/devices/${deviceId}`,
        { method: 'DELETE', headers }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to delete device' }));
        throw new Error(error.message || 'Failed to delete device');
      }

      devicesCache = null;
    } catch (error) {
      console.error('Error deleting device:', error);
      throw error;
    }
  },

  /**
   * Revoke device access (invalidates secret, disconnects immediately)
   */
  async revokeDevice(deviceId: string): Promise<void> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${CENTRAL_API_URL}/devices/${deviceId}/revoke`,
        { method: 'POST', headers }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || error.message || 'Failed to revoke device');
      }

      devicesCache = null;
    } catch (error) {
      console.error('Error revoking device:', error);
      throw error;
    }
  },

  /**
   * Undo a revoke within the restore window (#302). The device reconnects
   * with the credentials it already holds.
   */
  async restoreDevice(deviceId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${CENTRAL_API_URL}/devices/${deviceId}/restore`,
      { method: 'POST', headers }
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to restore device');
    }
    devicesCache = null;
  },

  /**
   * Assign edge device to a location
   */
  async assignToLocation(
    deviceId: string, 
    locationId: string,
    locationName?: string,
    countryId?: string,
    regionId?: string
  ): Promise<void> {
    return this.updateDeviceSettings(deviceId, { 
      locationId,
      locationName,
      countryId,
      regionId
    });
  },

  /**
   * Remove edge device from its location
   */
  async removeFromLocation(deviceId: string): Promise<void> {
    return this.updateDeviceSettings(deviceId, { locationId: null });
  },

  /**
   * Scan for available networks on a specific modem
   */
  async scanNetworks(deviceId: string, modemImei: string): Promise<{
    operators: Array<{
      mcc: string;
      mnc: string;
      name: string;
      country: string;
      technology: string;
      frequency?: string;
      signalStrength: number;
      signalQuality: number;
      available: boolean;
      registered: boolean;
      cellId?: string;
      lac?: string;
      arfcn?: number;
    }>;
  }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${RUNTIME_API_URL}/scanLocationNetworks`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            locationId: deviceId, // Using deviceId as locationId for now
            modemId: modemImei,
            organizationId: 'system' // Network scanning is system-level
          })
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to scan networks' }));
        throw new Error(error.message || 'Failed to scan networks');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error scanning networks:', error);
      throw error;
    }
  },

  /**
   * Get network information for a specific modem
   */
  async getModemNetworkInfo(deviceId: string, modemImei: string): Promise<{
    currentNetwork?: {
      operator: string;
      technology: string;
      signalStrength?: number;
      registrationStatus?: string;
    };
    availableNetworks: Array<{
      operator_name: string;
      mcc: string;
      mnc: string;
      network_type: string;
      network_generation: string;
      signal_strength?: number;
      timestamp: Date;
    }>;
    lastScanAt?: Date;
  }> {
    try {
      // Get modem data which now includes network info
      const modems = await this.getModems(deviceId);
      const modem = modems.find(m => m.imei === modemImei || m.modemId === modemImei);

      if (!modem) {
        throw new Error('Modem not found');
      }

      return {
        currentNetwork: modem.networkStatus ? {
          operator: modem.networkStatus.network || modem.networkStatus.operator || 'Unknown',
          technology: modem.networkStatus.type || modem.networkStatus.networkType || 'Unknown',
          signalStrength: modem.signalQuality?.rssiDbm ?? modem.signalQuality?.rssi,
          registrationStatus: modem.networkStatus.registrationStatus
        } : undefined,
        availableNetworks: (modem as any).availableNetworks || [],
        lastScanAt: (modem as any).lastNetworkScan
      };
    } catch (error) {
      console.error('Error getting modem network info:', error);
      throw error;
    }
  },

  /**
   * Get network analytics for all modems on a device
   */
  async getDeviceNetworkAnalytics(deviceId: string): Promise<{
    totalModems: number;
    modemsWithNetwork: number;
    availableNetworksCount: number;
    networkOperators: string[];
    networkTechnologies: string[];
    lastScanAt?: Date;
  }> {
    try {
      const modems = await this.getModems(deviceId);

      const modemsWithNetwork = modems.filter(m => m.networkStatus || (m.availableNetworks && m.availableNetworks.length > 0));

      // Collect all available networks
      const allNetworks = modems.flatMap(m => m.availableNetworks || []);
      const uniqueOperators = [...new Set(allNetworks.map(n => n.operator_name))];
      const uniqueTechnologies = [...new Set(allNetworks.map(n => n.network_generation))];

      // Find latest scan time
      const scanTimes = allNetworks.map(n => new Date(n.timestamp)).filter(t => !isNaN(t.getTime()));
      const lastScanAt = scanTimes.length > 0 ? new Date(Math.max(...scanTimes.map(t => t.getTime()))) : undefined;

      return {
        totalModems: modems.length,
        modemsWithNetwork: modemsWithNetwork.length,
        availableNetworksCount: allNetworks.length,
        networkOperators: uniqueOperators,
        networkTechnologies: uniqueTechnologies,
        lastScanAt
      };
    } catch (error) {
      console.error('Error getting device network analytics:', error);
      throw error;
    }
  },

  // =====================================================
  // Remote Management Methods
  // =====================================================

  /**
   * Move an edge device to another environment (prod <-> staging). Super-admin.
   * The device drains its tests, switches, and rolls itself back if it can't
   * connect to the target.
   */
  async switchEnvironment(
    deviceId: string,
    target: 'prod' | 'staging'
  ): Promise<{ success: boolean; actionId: string; mode: 'profile' | 'join' }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${CENTRAL_API_URL}/devices/${deviceId}/switch-environment`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ target }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to move the device' }));
      throw new Error(error.error || 'Failed to move the device');
    }
    return response.json();
  },

  /**
   * Send a system action (reboot, restart service) to an edge device
   */
  async sendSystemAction(
    deviceId: string,
    // issue #122: `reboot` / `restart_gateway` are graceful (drain in-flight
    // tests first). `force_reboot` skips the drain and reboots immediately.
    action: 'reboot' | 'restart_gateway' | 'restart_updater' | 'force_reboot' | 'enter_wifi_setup'
  ): Promise<{ success: boolean; actionId: string }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${CENTRAL_API_URL}/devices/${deviceId}/system-action`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ action })
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to send system action' }));
        throw new Error(error.error || 'Failed to send system action');
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending system action:', error);
      throw error;
    }
  },

  /**
   * Execute a whitelisted CLI command on an edge device
   */
  async sendCliCommand(
    deviceId: string,
    commandKey: string,
    params?: Record<string, string>
  ): Promise<{ success: boolean; commandId: string }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${CENTRAL_API_URL}/devices/${deviceId}/cli-command`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ commandKey, params })
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to send CLI command' }));
        throw new Error(error.error || 'Failed to send CLI command');
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending CLI command:', error);
      throw error;
    }
  },

  /**
   * Get CLI command result (poll until complete)
   */
  async getCliCommandResult(
    deviceId: string,
    commandId: string
  ): Promise<{ commandId: string; status: string; output: string; exitCode?: number; totalBytes?: number }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${CENTRAL_API_URL}/devices/${deviceId}/cli-command/${commandId}`,
        { headers }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to fetch command result' }));
        throw new Error(error.error || 'Failed to fetch command result');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching CLI command result:', error);
      throw error;
    }
  },

  /**
   * Create a shell session on an edge device
   */
  async createShellSession(deviceId: string): Promise<{ success: boolean; sessionId: string }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${CENTRAL_API_URL}/devices/${deviceId}/shell-session`,
        {
          method: 'POST',
          headers,
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to create shell session' }));
        throw new Error(error.error || 'Failed to create shell session');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating shell session:', error);
      throw error;
    }
  },

  /**
   * Terminate a shell session
   */
  async terminateShellSession(deviceId: string, sessionId: string): Promise<void> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${CENTRAL_API_URL}/devices/${deviceId}/shell-session/${sessionId}`,
        {
          method: 'DELETE',
          headers,
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to terminate session' }));
        throw new Error(error.error || 'Failed to terminate session');
      }
    } catch (error) {
      console.error('Error terminating shell session:', error);
      throw error;
    }
  },

  /**
   * Get the WebSocket URL for the shell session
   */
  getShellWebSocketUrl(sessionId: string, token: string): string {
    // Shell sessions ride the central-server WebSocket (the same instance that holds the
    // edge-device WS connections).
    return `${CENTRAL_WS_URL}?sessionId=${sessionId}&token=${token}`;
  },

  /**
   * Get available networks for a specific device (from AT+COPS=? scans)
   * This calls the /api/devices/{deviceId}/networks endpoint directly
   */
  async getDeviceAvailableNetworks(deviceId: string, modemId?: string): Promise<{
    scans: any[];
    networks: Array<{
      id: string;
      deviceId: string;
      modemId: string;
      scanId: string;
      operator_name: string;
      mcc: string;
      mnc: string;
      network_type: string;
      network_generation: string;
      is_available: boolean;
      timestamp: any;
    }>;
    summary: {
      totalScans: number;
      totalNetworks: number;
      latestScan: any | null;
    };
  }> {
    try {
      const headers = await getAuthHeaders();
      let url = `${CENTRAL_API_URL}/devices/${deviceId}/networks`;
      if (modemId) {
        url += `?modemId=${encodeURIComponent(modemId)}`;
      }

      const response = await fetch(url, { headers });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to fetch device networks' }));
        throw new Error(error.message || 'Failed to fetch device networks');
      }

      const data = await response.json();
      return data.data || {
        scans: [],
        networks: [],
        summary: { totalScans: 0, totalNetworks: 0, latestScan: null }
      };
    } catch (error) {
      console.error('Error fetching device available networks:', error);
      throw error;
    }
  }
};

